import React, { useContext, useEffect, useRef, useState } from 'react';
import { Alert, Button, Spinner } from 'react-bootstrap';
import { FaClock, FaSave, FaUser } from 'react-icons/fa';
import { useAppContext } from '../../context/AppContext.tsx';
import { AuthContext } from '../../context/AuthContext.js';
import RichTextEditor from '../../UIComponents/RichTextEditor.tsx';
import { getMyLocalTime } from '../../Utilities/Utilities.ts';
import { EmailTemplateModel } from './_core/_model.ts';
import { GetEmailTemplate, UpsertEmailTemplate } from './_core/_request.ts';

const TemplateEditor: React.FC = () => {
  const [selectedTemplate, setSelectedTemplate] = useState<number>(0);
  const [templates, setTemplates] = useState<EmailTemplateModel[]>([]);
  const [originalTemplates, setOriginalTemplates] = useState<EmailTemplateModel[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState('');
  const [errors, setErrors] = useState<{ name?: boolean; content?: boolean }>({});
  const [showSwitchWarning, setShowSwitchWarning] = useState(false);
  const [pendingSwitchIndex, setPendingSwitchIndex] = useState<number | null>(null);

  const initialContentRef = useRef<string[]>([]);
  const editorInitializedRef = useRef<Record<number, boolean>>({});
  const templatesLoaded = useRef(false);

  const { user } = useContext(AuthContext);
  const { allUsers } = useAppContext();

  useEffect(() => {
    setLoading(true);
    GetEmailTemplate(0).then((x) => {
      if (!templatesLoaded.current) {
        const loadedTemplates = [...x.data];
        setTemplates(loadedTemplates);
        setOriginalTemplates(JSON.parse(JSON.stringify(loadedTemplates)));
        initialContentRef.current = loadedTemplates.map(t => t.templateDetail || '');
        setLoading(false);
        templatesLoaded.current = true;
      }
    });
  }, []);

  const handleContentChange = (newContent: string) => {
    const index = selectedTemplate;
    const currentTemplate = templates[index];

    // Skip dirty check during initialization
    if (!editorInitializedRef.current[index]) {
      editorInitializedRef.current[index] = true;
      return;
    }

    // Compare with original content
    const originalValue = originalTemplates[index]?.templateDetail || '';
    const isContentChanged = newContent !== originalValue;

    if (isContentChanged !== dirty) {
      setDirty(isContentChanged);
    }

    // Update template content
    const updatedTemplates = [...templates];
    updatedTemplates[index].templateDetail = newContent;
    setTemplates(updatedTemplates);
  };

  const handleSubjectChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const prev = [...templates];
    prev[selectedTemplate].templateSub = e.target.value;
    setTemplates(prev);
    setDirty(true);
  };

  const handleTemplateSwitch = (index: number) => {
    // if (dirty) {
    //   setShowSwitchWarning(true);
    //   setPendingSwitchIndex(index);
    //   return;
    // }
    setSelectedTemplate(index);
    setError('');
    setErrors({});
  };

  const discardChanges = () => {
    if (pendingSwitchIndex !== null) {
      const reset = [...templates];
      reset[selectedTemplate] = JSON.parse(JSON.stringify(originalTemplates[selectedTemplate]));
      setTemplates(reset);
      setSelectedTemplate(pendingSwitchIndex);
      setPendingSwitchIndex(null);
      setShowSwitchWarning(false);
      setDirty(false);
    }
  };

  const handleSave = () => {
    const current = templates[selectedTemplate];
    let validationErrors: typeof errors = {};
    if (!current.templateName?.trim()) validationErrors.name = true;
    if (!current.templateDetail || current.templateDetail === '<p><br></p>') validationErrors.content = true;

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      setError('Please fix the highlighted fields.');
      return;
    }

    setSaving(true);
    setError('');
    setErrors({});

    UpsertEmailTemplate(current).then(() => {
      const updated = [...templates];
      updated[selectedTemplate].createdBy = user.userID;
      setTemplates([...updated]);
      const newOriginal = [...originalTemplates];
      newOriginal[selectedTemplate] = JSON.parse(JSON.stringify(current));
      setOriginalTemplates([...newOriginal]);
      setSaving(false);
      setDirty(false);
      setShowSwitchWarning(false);
    });
  };

  const getUsrName = (uId: number) => {
    const u = allUsers.find((x) => x.userID === uId);
    return u ? `${u.userFirstName} ${u.userLastName}` : '';
  };

  if (loading) return (
    <div className="container px-0 mx-0 bg-theme-card text-color">
      <div className="row">
        <div className="col-md-3 border-end pe-3">
          <div className="list-group">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="placeholder-glow mb-2">
                <div className="placeholder col-12" style={{ height: '2rem' }}></div>
              </div>
            ))}
          </div>
        </div>
        <div className="col-md-9">
          <div className="placeholder-glow mb-3">
            <div className="placeholder col-6 mb-2" style={{ height: '1rem' }}></div>
            <div className="placeholder col-12" style={{ height: '2.5rem' }}></div>
          </div>
          <div className="placeholder-glow mb-3">
            <div className="placeholder col-4 mb-2" style={{ height: '1rem' }}></div>
            <div className="placeholder col-12" style={{ height: '12rem' }}></div>
          </div>
          <div className="d-flex justify-content-between align-items-center mt-3">
            <div className="placeholder-glow">
              <div className="placeholder col-6" style={{ height: '1rem' }}></div>
            </div>
            <div className="placeholder-glow">
              <div className="placeholder col-4" style={{ height: '2.5rem' }}></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )

  const current = templates[selectedTemplate];

  return (
    <div className="container mb-4 mx-0 px-0 bg-theme-card text-color">
      {showSwitchWarning && (
        <Alert variant="warning" onClose={() => setShowSwitchWarning(false)} dismissible>
          <div className="d-flex justify-content-between align-items-center">
            <span>You have unsaved changes. Do you want to discard them and continue?</span>
            <Button size="sm" variant="outline-danger" onClick={discardChanges} className="ms-2">
              Discard & Continue
            </Button>
          </div>
        </Alert>
      )}

      <div className="row">
        <div className="col-md-3 border-end pe-3 j_st_mt_type">
          <div className="list-group">
            {templates.map((item, index) => (
              <button
                key={item.emailTemplateID}
                className={`list-group-item list-group-item-action ${selectedTemplate === index ? 'active' : ''}`}
                onClick={() => handleTemplateSwitch(index)}
              >
                {item.templateName}
              </button>
            ))}
          </div>
        </div>

        <div className="col-md-9">
          <div className="mb-3 j_st_mt_lbl">
            <label className="form-label fw-bold">Template Subject</label>
            <input
              className={`form-control ${errors.name ? 'is-invalid' : ''}`}
              value={current.templateSub}
              onChange={handleSubjectChange}
            />
            {errors.name && <div className="invalid-feedback">Template Subject is required.</div>}
          </div>
          <div className="small text-muted-theme">
            <strong>About:</strong> {current.templateDescription ? current.templateDescription : "This template is used to send Message to Admin."}
          </div>
        <div className='j_st_mt_desc'>
            <RichTextEditor 
                    value={current.templateDetail}
                    onChange={handleContentChange}
                    placeholder="Edit template content..."
                  />
        </div>
     
          {errors.content && <div className="text-danger mt-1">Template content is required.</div>}

          <div className="d-flex justify-content-between align-items-center mt-3">
            <div className="text-muted-theme small d-flex gap-3 align-items-center">
              <span><FaUser className="me-1" /> {getUsrName(current.createdBy)}</span>
              <span><FaClock className="me-1" /> {getMyLocalTime(current.createdAt)}</span>
            </div>
            <button className='btn btn-primary j_st_mt_save' onClick={handleSave} disabled={saving || !dirty}>
              {saving ? (
                <><Spinner animation="border" size="sm" className="me-2" />Saving...</>
              ) : (
                <><FaSave className="me-2" /> Save</>
              )}
            </button>
          </div>

          {error && <Alert variant="danger" className="mt-3">{error}</Alert>}
        </div>
      </div>
    </div>
  );
};

export default TemplateEditor;